import { useState } from "react";
import { Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";
import { setLateFee } from "../lib/api";
import { money, fmtDay } from "../lib/payments";

export const FeeAdjust = ({ payment, part, info, onChange }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const deal = info.fee_override;
  const tid = `${part}-${payment.id}`;

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await setLateFee(payment.id, part, value, note);
      onChange(data);
      toast.success(value === null ? "Fee deal removed — full 3% fee restored" : value === 0 ? "Late fee waived" : `Late fee set to ${money(value)}`);
      setOpen(false);
      setAmount("");
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not update fee");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full pl-8">
      {deal && (
        <p data-testid={`payment-fee-deal-${tid}`} className="font-mono text-[10px] text-emerald-300">
          Deal {fmtDay(deal.on)}: fee {deal.amount ? `set to ${money(deal.amount)}` : "waived"}{deal.note ? ` — ${deal.note}` : ""}
          {!info.paid_at && " · 3%/working day resumes if still unpaid"}
          <button data-testid={`payment-fee-clear-btn-${tid}`} onClick={() => save(null)} disabled={busy} className="ml-2 underline text-slate-400 hover:text-white">undo</button>
        </p>
      )}
      {!open ? (
        <button data-testid={`payment-fee-adjust-btn-${tid}`} onClick={() => setOpen(true)}
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded border border-[#384152] text-slate-200 hover:border-emerald-500">
          <Handshake className="h-3 w-3" /> Waive / reduce fee
        </button>
      ) : (
        <div data-testid={`payment-fee-form-${tid}`} className="mt-1 flex flex-wrap gap-2 items-center">
          <input data-testid={`payment-fee-amount-input-${tid}`} type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder={`New fee (now ${money(info.late_fee)})`}
            className="w-40 bg-[#0B0C0E] border border-[#262C38] rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500" />
          <input data-testid={`payment-fee-note-input-${tid}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            className="flex-1 min-w-[120px] bg-[#0B0C0E] border border-[#262C38] rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500" />
          <button data-testid={`payment-fee-save-btn-${tid}`} disabled={busy || amount === ""} onClick={() => save(Number(amount))}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-40">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Set fee"}
          </button>
          <button data-testid={`payment-fee-waive-btn-${tid}`} disabled={busy} onClick={() => save(0)}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded border border-emerald-600 text-emerald-300 disabled:opacity-40">Waive all</button>
          <button data-testid={`payment-fee-cancel-btn-${tid}`} onClick={() => setOpen(false)} className="text-[11px] text-slate-500 hover:text-white">Cancel</button>
        </div>
      )}
    </div>
  );
};
